import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withKeys as _withKeys, withCtx as _withCtx, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "inhdialog-header" }
const _hoisted_2 = { class: "dialog_name" }
const _hoisted_3 = { class: "dialog_content" }
const _hoisted_4 = {
  key: 0,
  class: "loading-container q-mt-sm"
}
const _hoisted_5 = { class: "field-group__row no-wrap justify-left" }
const _hoisted_6 = {
  key: 1,
  class: "inner_container"
}
const _hoisted_7 = { class: "field-group" }
const _hoisted_8 = { class: "field-group__row no-wrap justify-left" }
const _hoisted_9 = { class: "bold-name mr-xs" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { class: "field-group field-group--path" }
const _hoisted_12 = { class: "field-group__row field-group__row_path no-wrap justify-left items-center" }
const _hoisted_13 = { class: "bold-name mr-xs" }
const _hoisted_14 = {
  key: 0,
  class: "path-arrow-wrap",
  "aria-hidden": "true"
}
const _hoisted_15 = { key: 1 }
const _hoisted_16 = {
  key: 0,
  class: "field-group"
}
const _hoisted_17 = { class: "field-group__row no-wrap justify-left" }
const _hoisted_18 = { class: "bold-name" }
const _hoisted_19 = { class: "ei-total" }
const _hoisted_20 = { class: "mr-xs ei-datetime" }
const _hoisted_21 = {
  key: 1,
  class: "field-group"
}
const _hoisted_22 = { class: "field-group__row no-wrap justify-left" }
const _hoisted_23 = { class: "bold-name" }
const _hoisted_24 = { class: "ei-total" }
const _hoisted_25 = { class: "mr-xs ei-datetime" }

import { computed, onMounted, ref } from 'vue';
import useDialogPluginComponent from 'quasar/src/composables/use-dialog-plugin-component/use-dialog-plugin-component.js';;
import { BaseListItem } from './interfaces/ListTypes';
import { auxStore } from '@/store/auxillary';
import { AnalyticsElementDetailsDto, AnalyticsActionRecordDto, AnalyticsActionType } from '@/models/interfaces/entity-settings/charts/charts';
import { i18n } from '@/i18n';

const initialChunk = 8;
const maxStep = 20;


export default /*@__PURE__*/_defineComponent({
  __name: 'ElementInfoDialog',
  props: {
    element: {}
  },
  emits: [...useDialogPluginComponent.emits],
  setup(__props: any) {

const props = __props;

const { dialogRef, onDialogHide, onDialogOK } = useDialogPluginComponent();

// expose required bindings for QDialog
// eslint-disable-next-line vue/no-setup-props-destructure


const loading = ref(true);
const info = ref<AnalyticsElementDetailsDto | null>(null);

const store = auxStore();

const elementTitle = computed(() => {
  const el = props.element;
  switch (el.elType) {
    case 7: return i18n.global.t('info_subj_folder');
    case 0: return i18n.global.t('info_subj_selection');
    case 1: return i18n.global.t('info_subj_projection');
    case 2:
    case 3:
    case 4: return i18n.global.t('info_subj_chart');
    case 6: return i18n.global.t('info_subj_list');
    default: return i18n.global.t('info_subj_element');
  }
});

const createdAtText = computed(() => {
  if (!info.value?.CreateTime) return '';
  return formatDateTime(info.value.CreateTime);
});

const pathSegments = computed(() => {
  const sections = info.value?.Path?.Sections;
  const names = sections && Array.isArray(sections)
    ? sections.map((s: any) => s.ElementName)
    : [];
  const currentName = props.element?.Name || '';
  if (names.length) return currentName ? [...names, currentName] : names;
  return currentName ? [currentName] : [];
});

const views = computed<AnalyticsActionRecordDto[]>(() => info.value?.ReadActions || []);
const changes = computed<AnalyticsActionRecordDto[]>(() => info.value?.WriteActions || []);

const totalViews = computed(() =>
  info.value?.ReadActionsTotalCount ?? 0
);
const totalChanges = computed(() =>
  info.value?.WriteActionsTotalCount ?? 0
);

const viewsRemaining = computed(() =>
  Math.max(totalViews.value - views.value.length, 0)
);
const changesRemaining = computed(() =>
  Math.max(totalChanges.value - changes.value.length, 0)
);

const viewsMoreLabel = computed(() => {
  const rem = viewsRemaining.value;
  if (rem <= 0) return '';
  const total = totalViews.value;
  const count = total <= 28 ? rem : Math.min(maxStep, rem);
  return i18n.global.t('more_count_label', { count });
});

const changesMoreLabel = computed(() => {
  const rem = changesRemaining.value;
  if (rem <= 0) return '';
  const total = totalChanges.value;
  const count = total <= 28 ? rem : Math.min(maxStep, rem);
  return i18n.global.t('more_count_label', { count });
});

function formatDateTime(val: string | Date): string {
  if (!val) return '';
  const d = typeof val === 'string' ? new Date(val) : val;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes} ${day}.${month}.${year}`;
}

async function loadInfo() {
  loading.value = true;
  const { data } = await store.getAnalyticsElementInfo(props.element, {
    readCount: initialChunk,
    writeCount: initialChunk,
  });
  info.value = data || null;
  loading.value = false;
}

async function loadMoreViews() {
  if (viewsRemaining.value <= 0) return;
  const step =
    totalViews.value <= 28
      ? viewsRemaining.value
      : Math.min(maxStep, viewsRemaining.value);
  const { data } = await store.fetchAnalyticsActionsLog(
    props.element,
    AnalyticsActionType.Read,
    views.value.length,
    step,
  );
  if (info.value && Array.isArray(data) && data.length) {
    info.value = {
      ...info.value,
      ReadActions: [...views.value, ...data],
    };
  }
}

async function loadMoreChanges() {
  if (changesRemaining.value <= 0) return;
  const step =
    totalChanges.value <= 28
      ? changesRemaining.value
      : Math.min(maxStep, changesRemaining.value);
  const { data } = await store.fetchAnalyticsActionsLog(
    props.element,
    AnalyticsActionType.Write,
    changes.value.length,
    step,
  );
  if (info.value && Array.isArray(data) && data.length) {
    info.value = {
      ...info.value,
      WriteActions: [...changes.value, ...data],
    };
  }
}

function close() {
  onDialogOK();
}

onMounted(loadInfo);

return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    class: "element-info-dialog",
    "no-shake": "",
    "full-width": "",
    onHide: _unref(onDialogHide)
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "inhdialog-container element-info-dialog" }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('information_about')) + " " + _toDisplayString(elementTitle.value), 1),
            _createVNode(_component_q_btn, {
              flat: "",
              round: "",
              icon: "close",
              size: "md",
              color: "accent",
              title: _ctx.$t('close_label'),
              onClick: close
            }, null, 8, ["title"])
          ]),
          _createElementVNode("div", _hoisted_3, [
            (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (n) => {
                    return _createElementVNode("div", {
                      key: n,
                      class: "field-group"
                    }, [
                      _createElementVNode("div", _hoisted_5, [
                        _cache[0] || (_cache[0] = _createElementVNode("div", { class: "skeleton skeleton-title" }, null, -1)),
                        _createElementVNode("div", {
                          class: _normalizeClass(["skeleton skeleton-text", {
                  'skeleton-text-short': n % 3 === 0,
                  'skeleton-text-medium': n % 3 === 1,
                }])
                        }, null, 2)
                      ])
                    ])
                  }), 64))
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", _hoisted_8, [
                      _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.$t('created_at_label')) + ":", 1),
                      _createElementVNode("span", null, _toDisplayString(createdAtText.value), 1),
                      (info.value?.Creator?.Name)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_10, " [" + _toDisplayString(info.value.Creator.Name) + "]", 1))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, _toDisplayString(_ctx.$t('path_label')) + ":", 1),
                      (pathSegments.value.length)
                        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(pathSegments.value, (seg, idx) => {
                            return (_openBlock(), _createElementBlock(_Fragment, {
                              key: 'p_' + idx
                            }, [
                              _createElementVNode("span", null, _toDisplayString(seg), 1),
                              (idx < pathSegments.value.length - 1)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_14, _cache[1] || (_cache[1] = [
                                    _createElementVNode("svg", {
                                      xmlns: "http://www.w3.org/2000/svg",
                                      width: "16",
                                      height: "16",
                                      viewBox: "0 0 16 16",
                                      fill: "none"
                                    }, [
                                      _createElementVNode("path", {
                                        d: "M6 12L10 8L6 4",
                                        stroke: "#979797",
                                        "stroke-width": "2",
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round"
                                      })
                                    ], -1)
                                  ])))
                                : _createCommentVNode("", true)
                            ], 64))
                          }), 128))
                        : (_openBlock(), _createElementBlock("span", _hoisted_15, "—"))
                    ])
                  ]),
                  (views.value.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                        _createElementVNode("div", _hoisted_17, [
                          _createElementVNode("div", _hoisted_18, _toDisplayString(_ctx.$t('views_label')), 1),
                          _createElementVNode("span", _hoisted_19, _toDisplayString(_ctx.$t('total_count_label', { count: totalViews.value })), 1)
                        ]),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(views.value, (v, idx) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: 'view_' + idx,
                            class: "field-group__row no-wrap justify-left ei-log-row"
                          }, [
                            _createElementVNode("span", _hoisted_20, _toDisplayString(formatDateTime(v.CreateTime)), 1),
                            _createElementVNode("span", null, "[" + _toDisplayString(v.Creator?.Name) + "]", 1),
                            (idx === views.value.length - 1 && viewsRemaining.value > 0)
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 0,
                                  class: "ei-more",
                                  role: "button",
                                  tabindex: "0",
                                  onClick: loadMoreViews,
                                  onKeydown: _withKeys(loadMoreViews, ["enter"])
                                }, _toDisplayString(viewsMoreLabel.value), 33))
                              : _createCommentVNode("", true)
                          ]))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true),
                  (changes.value.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                        _createElementVNode("div", _hoisted_22, [
                          _createElementVNode("div", _hoisted_23, _toDisplayString(_ctx.$t('changes_label')), 1),
                          _createElementVNode("span", _hoisted_24, _toDisplayString(_ctx.$t('total_count_label', { count: totalChanges.value })), 1)
                        ]),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(changes.value, (c, idx) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: 'change_' + idx,
                            class: "field-group__row no-wrap justify-left ei-log-row"
                          }, [
                            _createElementVNode("span", _hoisted_25, _toDisplayString(formatDateTime(c.CreateTime)), 1),
                            _createElementVNode("span", null, "[" + _toDisplayString(c.Creator?.Name) + "]", 1),
                            (idx === changes.value.length - 1 && changesRemaining.value > 0)
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 0,
                                  class: "ei-more",
                                  role: "button",
                                  tabindex: "0",
                                  onClick: loadMoreChanges,
                                  onKeydown: _withKeys(loadMoreChanges, ["enter"])
                                }, _toDisplayString(changesMoreLabel.value), 33))
                              : _createCommentVNode("", true)
                          ]))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true)
                ]))
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["onHide"]))
}
}

})